import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import {
  COOKIE_APP_VERSION,
  COOKIE_AUTH,
  COOKIE_LOOP,
  COOKIE_TOKEN,
  COOKIE_USER_EMAIL,
  COOKIE_DISTRIBUTOR,
  getEnvPrefix,
} from './environment'
import type { IcpRegisterPendingStep } from '@/micro/types/register.type'

const getIcpFlowPendingCookieKey = () => `${getEnvPrefix()}-icp-flow-pending`

export const setCookie = (key: string, value: string, expires: number = 30) => {
  Cookies.set(key, value, {
    domain: '.alegra.com',
    path: '/',
    sameSite: 'strict',
    secure: true,
    expires,
  })
}

export const removeCookie = (key: string) => {
  Cookies.remove(key, {
    domain: '.alegra.com',
    path: '/',
  })
}

/**
 * Elimina la cookie de autenticación.
 */
export const clearApiTokenCookie = () => {
  try {
    removeCookie(COOKIE_AUTH)
    removeCookie(COOKIE_TOKEN)
  } catch (error) {}
}

/**
 * Valida si la cookie esta para redirigir a la APP.
 *
 * @returns {Boolean} Responde si la cookie existe.
 */
export const isValidApiTokenCookie = () => {
  const token = Cookies.get(COOKIE_TOKEN)
  // Si no existe el token retornamos false
  if (!token) return false
  // Si existe procedemos a verificar si aun no expira
  try {
    const decodedToken = jwtDecode(token)
    const currentTime = Date.now() / 1000
    return currentTime < decodedToken.exp!
  } catch (error) {
    // Si el token no se puede decodificar, considerarlo como expirado
    console.error('Auth token cannot be decoded:', error)
    return false
  }
}

/**
 * Valida si se puede redirigir a la APP.
 *
 * @returns {Boolean} respuesta para redirección.
 */
export const canRedirectToApp = () => {
  const counter = Cookies.get(COOKIE_LOOP)

  return (
    (!counter || Number(counter) <= 2) &&
    !window.location.pathname.includes('/user/password-reset/key')
  )
}

/**
 * Incrementa el contador de las redirecciones.
 */
export const incrementRedirectionCounter = () => {
  const counter = Cookies.get(COOKIE_LOOP)

  if (!counter) {
    setCookie(COOKIE_LOOP, '1')
  } else {
    setCookie(COOKIE_LOOP, (Number(counter) + 1).toString())
  }
}

/**
 * Limpia el localStorage de las 'keys' de alegra-company y alegra-user..
 */
export const clearNonAuthSessionData = () => {
  try {
    ;['alegra-company', 'alegra-user'].forEach(key => localStorage.removeItem(key))
  } catch (error) {
    console.error('Error clearing localStorage:', error)
  }

  ;[COOKIE_APP_VERSION, COOKIE_USER_EMAIL, COOKIE_LOOP].forEach(cookie => removeCookie(cookie))
}

export const clearDataOfLocalStorage = () => {
  clearNonAuthSessionData()
  clearApiTokenCookie()
}

export const setIcpFlowPendingStep = (step: IcpRegisterPendingStep) => {
  setCookie(getIcpFlowPendingCookieKey(), String(step))
}

export const getIcpFlowPendingStep = (): IcpRegisterPendingStep | null => {
  const value = Cookies.get(getIcpFlowPendingCookieKey())

  if (value === '1') return 1
  if (value === '2') return 2

  return null
}

export const hasIcpFlowPending = () => getIcpFlowPendingStep() !== null

export const clearIcpFlowPending = () => removeCookie(getIcpFlowPendingCookieKey())

/**
 * Retorna el id del distribuidor que esta en el cookie storage.
 *
 * @returns {String} ID del distribuidor
 */
export const readIdDistributorFromStorage = () => Cookies.get(COOKIE_DISTRIBUTOR)
